/**
 * Picking several users and groups: the chosen ones as removable chips, a
 * `SubjectPicker` under them to add another.
 *
 * Fetch-free like `SubjectPicker`, and for the same reason: the options come
 * from whatever search the caller runs — pair it with `useSubjectSearch`.
 * Options already chosen are left out of the list, and the picker remounts
 * after each pick so what was typed clears.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import type { SubjectDto } from '@forsys/shared';
import { subjectKey } from '../utils/subject';
import { SubjectIcon } from './SubjectIcon';
import { SubjectPicker } from './SubjectPicker';

export interface SubjectMultiPickerProps {
  value: readonly SubjectDto[];
  onChange: (subjects: SubjectDto[]) => void;
  /** What the search returned for the current query, chosen ones included. */
  options: SubjectDto[];
  loading?: boolean;
  /** Defaults to `SubjectPicker`'s "User or group". */
  label?: string;
  helperText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Raises on every keystroke. Debounce it in the caller — `useSubjectSearch` does. */
  onQueryChange: (query: string) => void;
}

export function SubjectMultiPicker({
  value,
  onChange,
  options,
  loading = false,
  label,
  helperText,
  disabled = false,
  autoFocus = false,
  onQueryChange,
}: SubjectMultiPickerProps) {
  const chosen = new Set(value.map(subjectKey));

  return (
    <Stack spacing={1}>
      {value.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {value.map((subject) => (
            <Chip
              key={subjectKey(subject)}
              icon={<SubjectIcon type={subject.type} />}
              label={subject.displayName}
              disabled={disabled}
              onDelete={() => onChange(value.filter((one) => subjectKey(one) !== subjectKey(subject)))}
            />
          ))}
        </Stack>
      )}
      <SubjectPicker
        key={value.length}
        options={options.filter((option) => !chosen.has(subjectKey(option)))}
        value={null}
        loading={loading}
        {...(label ? { label } : {})}
        {...(helperText ? { helperText } : {})}
        disabled={disabled}
        autoFocus={autoFocus}
        onChange={(subject) => {
          if (subject && !chosen.has(subjectKey(subject))) onChange([...value, subject]);
          onQueryChange('');
        }}
        onQueryChange={onQueryChange}
      />
    </Stack>
  );
}
